import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import SurgeryPrescriptionDetailsEdit from "./SurgeryPrescriptionDetailsEdit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const SurgeryPrescriptionList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Pagination state variables
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPrescriptions, setTotalPrescriptions] = useState(0); // Total count

  const { handleDelete: handleDeletePrescription, ConfirmDialog } = useDelete(
    "surgery-prescriptions",
    token
  );
  const {
    handleDelete: handleDeletePrescriptionItem,
    ConfirmDialog: ConfirmDialogItem,
  } = useDelete("surgery-prescription-details", token);

  const { data, isLoading, isError, isSuccess, error, refetch } =
    useFetchObjects(
      ["surgery-prescriptions"],
      `surgery-prescriptions/?search=${encodeURIComponent(
        searchQuery
      )}&page=${page}&page_size=${rowsPerPage}`,
      token
    );

  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalPrescriptions(data.count);
    }
  }, [data]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  if (isLoading) {
    return <Typography>{t("pharmacy.loading")}</Typography>;
  }

  if (isError) {
    return <Typography>{error.message}</Typography>;
  }
  if (isSuccess) {
    const prescriptions = data?.results || [];
    return (
      <Grid container spacing={3} padding="20px" mt={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isSmallScreen ? "stretch" : "center"}
            mb={3}
            gap={isSmallScreen ? 2 : 0}
          >
            {/* Back Icon and Title */}
            <Box display="flex" alignItems="center">
              <Typography
                variant="h5"
                fontWeight="bold"
                color="primary"
                sx={{ flex: isSmallScreen ? "none" : "1 1 auto" }}
              >
                {t("pharmacy.SurgeryPrescriptionList")}
              </Typography>
            </Box>

            {/* Add New Button */}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/pharmacy/surgery-prescription/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: "#007174",
                "&:hover": {
                  backgroundColor: "#007174",
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("pharmacy.addNew")}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {/* Search Bar */}
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("pharmacy.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              "aria-label": t("pharmacy.search"),
            }}
            sx={{
              flex: isSmallScreen ? "none" : "2 1 auto",
              width: isSmallScreen ? "100%" : "auto",
            }}
          />
        </Grid>
        {prescriptions.map((prescription, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              elevation={4}
              sx={{
                mb: 3,
                padding: "24px",
                borderRadius: "12px",
                width: "100%",
                backgroundColor: "#f4f6f8",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="primary"
                  sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                >
                  {t("pharmacy.prescription")} #{prescription.id} -{" "}
                  {prescription.date}
                </Typography>
                <Stack direction="row" justifyContent="flex-end">
                  <Tooltip title={t("pharmacy.delete_prescription")}>
                    <IconButton
                      color="error"
                      onClick={() => handleDeletePrescription(prescription.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Stack direction="column" spacing={1} sx={{ mt: 2 }}>
                <Typography>
                  <strong>{t("pharmacy.surgeryType")}:</strong>{" "}
                  {prescription.surgery_details?.surgery_type_display}
                </Typography>
                <Typography>
                  <strong>{t("pharmacy.total_price")}:</strong>{" "}
                  {prescription.calculate_total_price} ؋
                </Typography>
              </Stack>

              <Divider sx={{ mt: 2, mb: 2 }} />

              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography fontWeight="bold">
                    {t("pharmacy.medicines")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {prescription.details?.length > 0 ? (
                    <Box sx={{ overflowX: "auto" }}>
                      <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("pharmacy.category")}</TableCell>
                            <TableCell>{t("pharmacy.medicine")}</TableCell>
                            <TableCell>{t("pharmacy.quantity")}</TableCell>
                            <TableCell>{t("pharmacy.price")}</TableCell>
                            <TableCell>{t("pharmacy.purchasePrice")}</TableCell>
                            <TableCell>{t("pharmacy.total_price")}</TableCell>
                            <TableCell align="center">
                              {t("pharmacy.actions")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {prescription.details.map((item) => (
                            <TableRow
                              key={item.id}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#f0f4f8",
                                },
                              }}
                            >
                              <TableCell>
                                <Typography>
                                  {item.stock_medicine_category}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography>
                                  {item.stock_medicine_name}
                                </Typography>
                              </TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>{item.stock_medicine_price}</TableCell>
                              <TableCell>
                                {item.stock_medicine?.purchase_price || "N/A"}
                              </TableCell>
                              <TableCell>{item.calculate_price}</TableCell>
                              <TableCell align="center">
                                <Tooltip title={t("pharmacy.edit_item")}>
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleOpen(item)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("pharmacy.delete_item")}>
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      handleDeletePrescriptionItem(item.id)
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Typography>{t("pharmacy.no_medicines")}</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Grid>
        ))}

        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={10}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("pharmacy.rowsPerPage")}: </Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                size="small"
              >
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPrescriptions / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>

        <ConfirmDialog />
        <ConfirmDialogItem />

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>
            <Typography fontWeight="bold" color="primary">
              {t("pharmacy.edit_prescription_item")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "20px" }}>
            {selectedItem && (
              <SurgeryPrescriptionDetailsEdit
                prescriptionDetail={selectedItem}
                onClose={handleClose}
                token={token}
              />
            )}
          </DialogContent>
        </Dialog>
      </Grid>
    );
  }
};

export default SurgeryPrescriptionList;
